import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Loader2, Wallet } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { PaymentsTable, PAYMENT_STATUS, fmtPeriod } from './PaymentsTable';

const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? 'Revisa los campos' : 'No se pudo guardar'; };
const FIELD_LABELS = { period: 'Período', amount: 'Monto', status: 'Estado', payment_date: 'Fecha de pago', method: 'Método', reference: 'Referencia', notes: 'Notas' };

function PaymentForm({ initial, onCancel, onSave, saving }) {
    const [f, setF] = useState(initial);
    const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Período</Label><Input type="month" value={f.period} onChange={set('period')} disabled={!!initial.id} data-testid="payment-period" /></div>
                <div className="space-y-1.5"><Label>Monto facturado (USD)</Label><Input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} data-testid="payment-amount" /></div>
                <div className="space-y-1.5"><Label>Estado</Label>
                    <Select value={f.status} onValueChange={set('status')}>
                        <SelectTrigger data-testid="payment-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(PAYMENT_STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5"><Label>Fecha de pago</Label><Input type="date" value={f.payment_date || ''} onChange={set('payment_date')} data-testid="payment-date" /></div>
                <div className="space-y-1.5"><Label>Método</Label><Input placeholder="Transferencia, cheque…" value={f.method} onChange={set('method')} data-testid="payment-method" /></div>
                <div className="space-y-1.5"><Label>Referencia</Label><Input value={f.reference} onChange={set('reference')} data-testid="payment-reference" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Textarea rows={2} value={f.notes} onChange={set('notes')} data-testid="payment-notes" /></div>
            <DialogFooter>
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button disabled={saving || !f.period || f.amount === ''} onClick={() => onSave({ ...f, amount: Number(f.amount), payment_date: f.payment_date || null })} data-testid="payment-save">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
                </Button>
            </DialogFooter>
        </div>
    );
}

function HistoryList({ items }) {
    if (!items) return <p className="text-sm text-muted-foreground">Cargando…</p>;
    return (
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="payment-history-list">
            {items.map(h => (
                <div key={h.id} className="rounded border border-border/50 p-2 text-xs">
                    <p className="font-semibold">{h.action === 'create' ? 'Alta' : 'Cambio'} · {h.user_name || h.user_email} · <span className="font-mono">{new Date(h.created_at).toLocaleString('es')}</span></p>
                    {Object.entries(h.changes || {}).map(([k, v]) => {
                        const fmt = (x) => k === 'status' ? (PAYMENT_STATUS[x]?.label || x || '—') : String(x ?? '—');
                        return <p key={k} className="text-muted-foreground">{FIELD_LABELS[k] || k}: {fmt(v.before)} → <span className="text-foreground">{fmt(v.after)}</span></p>;
                    })}
                </div>
            ))}
        </div>
    );
}

export function ManualPaymentsCard({ tenantId, suggestedAmount, apiCall }) {
    const [payments, setPayments] = useState([]);
    const [editing, setEditing] = useState(null);
    const [history, setHistory] = useState(null);
    const [saving, setSaving] = useState(false);
    const load = useCallback(() => apiCall('get', `/admin/billing/payments?tenant_id=${tenantId}`).then(r => setPayments(r.data)).catch(() => toast.error('No se pudieron cargar los pagos')), [apiCall, tenantId]);
    useEffect(() => { load(); }, [load]);

    const save = async (f) => {
        setSaving(true);
        try {
            if (f.id) {
                const { amount, status, payment_date, method, reference, notes } = f;
                await apiCall('patch', `/admin/billing/payments/${f.id}`, { amount, status, payment_date, method, reference, notes });
                toast.success('Pago actualizado');
            } else {
                await apiCall('post', `/admin/billing/payments?tenant_id=${tenantId}`, f);
                toast.success('Pago registrado');
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
    };
    const openHistory = async (p) => {
        setHistory({ payment: p, items: null });
        try { const r = await apiCall('get', `/admin/billing/payments/${p.id}/history`); setHistory({ payment: p, items: r.data }); }
        catch { toast.error('No se pudo cargar el historial'); }
    };
    const blank = { period: thisMonth(), amount: suggestedAmount ?? '', status: 'pending', payment_date: '', method: '', reference: '', notes: '' };

    return (
        <div className="rounded-lg border border-border/60 p-4 space-y-3" data-testid="manual-payments-card">
            <div className="flex items-center justify-between">
                <p className="text-sm font-semibold flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" />Pagos manuales</p>
                <Button size="sm" variant="outline" onClick={() => setEditing(blank)} data-testid="payment-add-btn"><Plus className="w-4 h-4 mr-1" />Registrar mes</Button>
            </div>
            <PaymentsTable payments={payments} onEdit={(p) => setEditing({ ...p, payment_date: p.payment_date || '' })} onHistory={openHistory} testid="admin-payments-table" />
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="sm:max-w-[560px]" data-testid="payment-dialog">
                    <DialogHeader><DialogTitle>{editing?.id ? `Editar ${fmtPeriod(editing.period)}` : 'Registrar pago del mes'}</DialogTitle></DialogHeader>
                    {editing && <PaymentForm initial={editing} onCancel={() => setEditing(null)} onSave={save} saving={saving} />}
                </DialogContent>
            </Dialog>
            <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
                <DialogContent className="sm:max-w-[520px]" data-testid="payment-history-dialog">
                    <DialogHeader><DialogTitle>Historial · {history && fmtPeriod(history.payment.period)}</DialogTitle></DialogHeader>
                    {history && <HistoryList items={history.items} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function BillingMessageEditor({ apiCall }) {
    const [msg, setMsg] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => { apiCall('get', '/admin/billing/settings').then(r => setMsg(r.data.billing_message)).catch(() => {}); }, [apiCall]);
    const save = async () => {
        setSaving(true);
        try { await apiCall('put', '/admin/billing/settings', { billing_message: msg }); toast.success('Mensaje de facturación actualizado'); }
        catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
    };
    return (
        <div className="space-y-2" data-testid="billing-message-editor">
            <Label>Mensaje que ven las aerolíneas en Facturación</Label>
            <Textarea rows={2} value={msg} onChange={e => setMsg(e.target.value)} maxLength={500} data-testid="billing-message-input" />
            <Button size="sm" onClick={save} disabled={saving || !msg.trim()} data-testid="billing-message-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar mensaje</Button>
        </div>
    );
}
